import { useEffect, useState } from 'react';
import { FormField, FormSelect, SettingsSection, TextInput, Toggle } from '@/components/ui';
import { Icon } from '@/components/ui/Icon';
import { toast } from '@/components/ui/Toast';
import { adminApi, type FireberryField, type FireberryFieldMap, type FireberryImportResult, type FireberryImportSettings, type FireberryInboundKey, type FireberryLeadTable, type FireberryObject, type FireberrySettings } from '@/shared/api/adminApi';

/**
 * חיבור Fireberry CRM — הגדרות → חברה → מתג FIREBERRY.
 *
 * המתג פותח את השדות (TokenID, כתובת API, אובייקט יעד), "בדוק חיבור" מוודא
 * מול פיירברי לפני שמירה, ואחרי חיבור תקין:
 *   · "התקן אוטומציות מומלצות" — 3 אוטומציות מוכנות (ליד חדש → לקוח,
 *     שיחה הסתיימה → רישום שיחה, תיעוד → הערה) בלשונית אוטומציות.
 *   · מפתח לפיירברי — כדי שפיירברי תשלח אלינו לידים (POST /v1/leads).
 * הטוקן נשמר מוצפן בשרת ולא חוזר לדפדפן (token_set בלבד).
 */
const BASE_URLS: { value: string; label: string }[] = [
  { value: 'https://api.fireberry.com/api/', label: 'api.fireberry.com (ברירת מחדל)' },
  { value: 'https://api.powerlink.co.il/api/', label: 'api.powerlink.co.il (חשבונות ותיקים)' },
];

export function FireberrySettingsSection({ initial, onAutomationsChanged }: {
  initial?: FireberrySettings;
  /** נקרא אחרי התקנת אוטומציות מומלצות — כדי שהלשונית תתרענן. */
  onAutomationsChanged?: () => void;
}) {
  const [enabled, setEnabled] = useState(initial?.enabled ?? false);
  const [baseUrl, setBaseUrl] = useState(initial?.base_url ?? BASE_URLS[0].value);
  const [leadObject, setLeadObject] = useState<'contact' | 'account'>(initial?.lead_object ?? 'contact');
  const [token, setToken] = useState('');
  const [tokenSet, setTokenSet] = useState(initial?.token_set ?? false);
  const [tokenHint, setTokenHint] = useState(initial?.token_hint ?? null);
  const [configured, setConfigured] = useState(initial?.configured ?? false);
  const [inbound, setInbound] = useState(initial?.inbound ?? { url: '', key_prefix: null });
  const [presets, setPresets] = useState(initial?.presets_installed ?? 0);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [newKey, setNewKey] = useState<FireberryInboundKey | null>(null);
  const [installing, setInstalling] = useState(false);
  // הקמפיין שאליו פיירברי תכניס לידים — לפי מזהה (campaign_id), לא לפי שם.
  const [inboundCampaigns, setInboundCampaigns] = useState<Array<{ id: number; name: string }>>([]);
  const [inboundCampaignId, setInboundCampaignId] = useState<number | null>(initial?.import?.campaign_id ?? null);
  useEffect(() => {
    if (!configured) return;
    adminApi.campaigns().then((r) => setInboundCampaigns(((r.data ?? []) as Array<{ id: number; name: string }>).map((c) => ({ id: c.id, name: c.name })))).catch(() => {});
  }, [configured]);
  const inboundBody = (k: FireberryInboundKey): Record<string, unknown> => {
    const { campaign: _dropName, ...rest } = k.example_body as Record<string, unknown>;
    void _dropName;
    return { ...rest, campaign_id: inboundCampaignId ?? '<מזהה הקמפיין ב-GoCall>' };
  };

  const apply = (s: FireberrySettings) => {
    setTokenSet(s.token_set); setTokenHint(s.token_hint ?? null); setConfigured(s.configured);
    setInbound(s.inbound); setPresets(s.presets_installed); setEnabled(s.enabled);
  };

  async function save(nextEnabled = enabled) {
    if (nextEnabled && !token && !tokenSet) { toast('הזן TokenID מפיירברי (הגדרות → ממשקי אינטרנט)', 'danger'); return; }
    setSaving(true);
    try {
      const res = await adminApi.updateFireberry({
        enabled: nextEnabled, base_url: baseUrl, lead_object: leadObject, ...(token ? { token } : {}),
      });
      apply(res);
      setToken('');
      toast(nextEnabled ? 'החיבור לפיירברי נשמר' : 'החיבור לפיירברי כבוי', 'ok');
    } catch (e: unknown) {
      toast((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'שגיאה בשמירה', 'danger');
    } finally { setSaving(false); }
  }

  async function toggle(v: boolean) {
    setEnabled(v);
    // כיבוי נשמר מיד; הדלקה דורשת Token — נשמרת בכפתור.
    if (!v && tokenSet) await save(false);
  }

  async function test() {
    if (!token && !tokenSet) { toast('הזן TokenID לפני בדיקה', 'danger'); return; }
    setTesting(true);
    setTestResult(null);
    try {
      setTestResult(await adminApi.testFireberry({ base_url: baseUrl, ...(token ? { token } : {}) }));
    } catch {
      setTestResult({ ok: false, message: 'שגיאה בבדיקת החיבור' });
    } finally { setTesting(false); }
  }

  async function clear() {
    setSaving(true);
    try {
      apply(await adminApi.deleteFireberry());
      setToken(''); setTestResult(null);
      toast('החיבור לפיירברי הוסר', 'ok');
    } catch { toast('שגיאה בהסרה', 'danger'); } finally { setSaving(false); }
  }

  async function createKey() {
    try {
      const k = await adminApi.createFireberryInboundKey();
      setNewKey(k);
      setInbound({ ...inbound, url: k.url, key_prefix: k.prefix });
    } catch { toast('שגיאה ביצירת המפתח', 'danger'); }
  }

  async function installPresets() {
    setInstalling(true);
    try {
      const r = await adminApi.installFireberryPresets();
      setPresets((p) => p + r.created.length);
      toast(r.created.length ? `נוצרו ${r.created.length} אוטומציות — לשונית אוטומציות` : 'האוטומציות המומלצות כבר קיימות', 'ok');
      onAutomationsChanged?.();
    } catch (e: unknown) {
      toast((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'שגיאה בהתקנה', 'danger');
    } finally { setInstalling(false); }
  }

  const copy = (text: string) => { void navigator.clipboard?.writeText(text).then(() => toast('הועתק', 'ok')).catch(() => {}); };

  return (
    <SettingsSection
      title="FIREBERRY"
      icon="link"
      selfSaving
      badge={configured ? { label: 'מחובר', tone: 'ok' } : tokenSet ? { label: 'כבוי', tone: 'neutral' } : undefined}
      description="חיבור טבעי ל-Fireberry CRM: לקוחות, שיחות והערות נכתבים לפיירברי מהאוטומציות, ולידים מפיירברי נכנסים לקמפיינים."
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', background: 'var(--surface-2)', borderRadius: 'var(--r-2)', border: '1px solid var(--line)', marginBottom: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, letterSpacing: '0.04em' }}>FIREBERRY</div>
          <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginTop: 2 }}>
            {enabled ? 'דלוק — פעולות פיירברי זמינות באוטומציות' : 'כבוי — פעולות פיירברי באוטומציות נעולות'}
          </div>
        </div>
        <Toggle checked={enabled} onChange={(v) => void toggle(v)} />
      </div>

      {enabled && (
        <div data-testid="fireberry-fields" className="stack">
          <FormField
            label={tokenSet ? `TokenID (נשמר: ${tokenHint ?? '••••'} — הזן כדי להחליף)` : 'TokenID'}
            hint="בפיירברי: הגדרות (גלגל השיניים) → ממשקי אינטרנט → TokenID. לכל משתמש טוקן משלו — מומלץ משתמש ייעודי לאינטגרציה."
          >
            <TextInput type="password" value={token} onChange={(e) => setToken(e.target.value)} dir="ltr" placeholder={tokenSet ? '••••••••••••' : 'TokenID מפיירברי'} aria-label="TokenID" />
          </FormField>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 'var(--gap)' }}>
            <FormField label="כתובת ה-API">
              <FormSelect value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} aria-label="כתובת API">
                {BASE_URLS.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
              </FormSelect>
            </FormField>
            <FormField label="לאן נכנסים לקוחות חדשים" hint="לפי טלפון: איש קשר (contact) או לקוח/חשבון (account)">
              <FormSelect value={leadObject} onChange={(e) => setLeadObject(e.target.value as 'contact' | 'account')} aria-label="אובייקט יעד">
                <option value="contact">אנשי קשר (contact)</option>
                <option value="account">לקוחות / חשבונות (account)</option>
              </FormSelect>
            </FormField>
          </div>

          {testResult && (
            <div data-testid="fireberry-test-result" style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 10px', borderRadius: 'var(--r-2)', fontSize: 'var(--t-sm)', background: 'var(--surface-2)', border: `1px solid ${testResult.ok ? 'var(--ok, var(--line))' : 'var(--danger)'}`, color: testResult.ok ? 'var(--ok)' : 'var(--danger)' }}>
              <Icon name={testResult.ok ? 'ok' : 'danger'} size={15} color={testResult.ok ? 'var(--ok)' : 'var(--danger)'} />
              <span>{testResult.message}</span>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" className="btn primary" onClick={() => void save(true)} disabled={saving}>{saving ? 'שומר…' : 'שמור חיבור'}</button>
            <button type="button" className="btn" onClick={() => void test()} disabled={testing}>{testing ? 'בודק…' : 'בדוק חיבור'}</button>
            {tokenSet && <button type="button" className="btn ghost" onClick={() => void clear()} disabled={saving} style={{ color: 'var(--danger)' }}>הסר חיבור</button>}
          </div>

          {configured && (
            <>
              {/* ── אוטומציות מומלצות ── */}
              <div style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-2)', padding: 12, marginTop: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>אוטומציות מומלצות לפיירברי</div>
                    <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginTop: 2 }}>
                      ליד חדש → לקוח בפיירברי · שיחה הסתיימה → רישום שיחה · תיעוד שיחה → הערה ללקוח.
                      {presets > 0 ? ` (${presets} מותקנות)` : ''}
                    </div>
                  </div>
                  <button type="button" className="btn primary" onClick={() => void installPresets()} disabled={installing}>
                    {installing ? 'מתקין…' : presets >= 3 ? 'מותקנות ✓' : 'התקן אוטומציות מומלצות'}
                  </button>
                </div>
                <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginTop: 6 }}>
                  אפשר לערוך אותן, ולבנות פעולות פיירברי משלך, בלשונית <strong>אוטומציות</strong>.
                </div>
              </div>

              {/* ── מאיזו טבלה לוקחים לידים + מיפוי שדות + ייבוא לקמפיין ── */}
              <LeadTableBlock
                initialTable={initial?.lead_table ?? null}
                initialMap={initial?.field_map ?? {}}
                initialImport={initial?.import}
                onSaved={apply}
              />

              {/* ── לידים מפיירברי אלינו ── */}
              <div style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-2)', padding: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>לידים מפיירברי אל GoCall</div>
                    <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginTop: 2 }}>
                      {inbound.key_prefix
                        ? <>מפתח פעיל <code dir="ltr">{inbound.key_prefix}…</code>{inbound.last_used_at ? ` · שימוש אחרון ${new Date(inbound.last_used_at).toLocaleString('he-IL')}` : ' · עדיין לא נעשה בו שימוש'}</>
                        : 'עדיין אין מפתח — צור מפתח ייעודי לפיירברי (הרשאת לידים בלבד).'}
                    </div>
                  </div>
                  <button type="button" className="btn" onClick={() => void createKey()}>{inbound.key_prefix ? 'החלף מפתח' : 'צור מפתח לפיירברי'}</button>
                </div>
                <div style={{ marginTop: 8, display: 'grid', gridTemplateColumns: 'minmax(220px, 360px)', gap: 8 }}>
                  <FormField label="הקמפיין שאליו הלידים נכנסים" hint="הזיהוי בבקשה הוא לפי מזהה הקמפיין (campaign_id), לא לפי שם — שינוי שם לא ישבור את החיבור.">
                    <FormSelect value={inboundCampaignId ?? ''} onChange={(e) => setInboundCampaignId(e.target.value ? Number(e.target.value) : null)} aria-label="קמפיין ללידים נכנסים">
                      <option value="">בחר קמפיין…</option>
                      {inboundCampaigns.map((c) => <option key={c.id} value={c.id}>{c.name} (ID {c.id})</option>)}
                    </FormSelect>
                  </FormField>
                </div>
                {newKey && (
                  <div data-testid="fireberry-inbound-key" style={{ marginTop: 10, background: 'var(--surface-2)', borderRadius: 'var(--r-2)', padding: 10, fontSize: 'var(--t-sm)' }}>
                    <div style={{ color: 'var(--danger)', fontWeight: 600, marginBottom: 6 }}>המפתח מוצג פעם אחת בלבד — העתק אותו עכשיו.</div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <code dir="ltr" style={{ flex: 1, wordBreak: 'break-all' }}>{newKey.key}</code>
                      <button type="button" className="btn btn-sm" onClick={() => copy(newKey.key)}>העתק</button>
                    </div>
                    <div style={{ marginTop: 10, fontWeight: 600 }}>איך מגדירים בפיירברי</div>
                    <ol style={{ margin: '4px 0 0', paddingInlineStart: 18, display: 'grid', gap: 2 }}>
                      <li>אוטומציה בפיירברי על יצירת ליד/לקוח → פעולה "קריאה ל-Webhook / URL חיצוני".</li>
                      <li>שיטה POST לכתובת <code dir="ltr">{newKey.url}</code>.</li>
                      <li>כותרות: <code dir="ltr">X-Api-Key: {'<המפתח>'}</code>, <code dir="ltr">Content-Type: application/json</code>, <code dir="ltr">Accept: application/json</code>.</li>
                      <li>גוף JSON עם השדות של הרשומה (phone חובה; <code dir="ltr">campaign_id</code> = מזהה הקמפיין שנבחר למעלה{inboundCampaignId ? ` — ${inboundCampaignId}` : ''}):</li>
                    </ol>
                    <pre dir="ltr" data-testid="fireberry-inbound-example" style={{ margin: '6px 0 0', fontSize: 'var(--t-xs)', whiteSpace: 'pre-wrap', background: 'var(--surface)', padding: 8, borderRadius: 'var(--r-2)' }}>
                      {JSON.stringify(inboundBody(newKey), null, 2)}
                    </pre>
                    <button type="button" className="btn btn-sm" style={{ marginTop: 6 }} onClick={() => copy(
                      `curl -X POST '${newKey.url}' -H 'X-Api-Key: ${newKey.key}' -H 'Content-Type: application/json' -H 'Accept: application/json' --data '${JSON.stringify(inboundBody(newKey))}'`,
                    )}>העתק דוגמת curl</button>
                    <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginTop: 6 }}>
                      כל בקשה שמגיעה נראית ב-הגדרות → API → בודק בקשות, ואצל מנהל-העל תחת מפתחות API.
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </SettingsSection>
  );
}


const MAP_LABELS: Array<{ key: keyof FireberryFieldMap; label: string; hint?: string }> = [
  { key: 'phone', label: 'טלפון (חובה)', hint: 'לפי השדה הזה מזהים את הליד' },
  { key: 'phone2', label: 'טלפון נוסף' },
  { key: 'name', label: 'שם / שם פרטי' },
  { key: 'last_name', label: 'שם משפחה' },
  { key: 'email', label: 'אימייל' },
  { key: 'status', label: 'סטטוס' },
  { key: 'created', label: 'תאריך יצירה', hint: 'לייבוא "רק חדשים"' },
  { key: 'modified', label: 'תאריך עדכון', hint: 'לייבוא כולל עדכונים' },
  { key: 'id', label: 'מזהה הרשומה', hint: 'ריק = _id' },
];

/**
 * בחירת טבלת הלידים בפיירברי (כולל טבלאות מותאמות אישית), מיפוי השדות שלה,
 * וייבוא מתוזמן של הרשומות לקמפיין ב-GoCall.
 */
function LeadTableBlock({ initialTable, initialMap, initialImport, onSaved }: {
  initialTable: FireberryLeadTable | null;
  initialMap: FireberryFieldMap;
  initialImport?: FireberryImportSettings;
  onSaved: (s: FireberrySettings) => void;
}) {
  const [objects, setObjects] = useState<FireberryObject[] | null>(null);
  const [loadingObjects, setLoadingObjects] = useState(false);
  const [table, setTable] = useState<FireberryLeadTable | null>(initialTable);
  const [fields, setFields] = useState<FireberryField[] | null>(null);
  const [map, setMap] = useState<FireberryFieldMap>(initialMap);
  const [imp, setImp] = useState<FireberryImportSettings>(initialImport ?? { enabled: false, campaign_id: null, interval_min: 5, only_new: true });
  const [campaigns, setCampaigns] = useState<Array<{ id: number; name: string }>>([]);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState<'run' | 'preview' | null>(null);
  const [result, setResult] = useState<FireberryImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    adminApi.campaigns().then((r) => setCampaigns(((r.data ?? []) as Array<{ id: number; name: string }>).map((c) => ({ id: c.id, name: c.name })))).catch(() => {});
  }, []);

  async function loadObjects() {
    setLoadingObjects(true); setError(null);
    try { setObjects(await adminApi.fireberryObjects()); }
    catch (e: unknown) { setError((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'לא ניתן לטעון את הטבלאות מפיירברי'); }
    finally { setLoadingObjects(false); }
  }

  async function chooseTable(objectType: number) {
    const obj = objects?.find((o) => o.object_type === objectType);
    const next: FireberryLeadTable = { object_type: objectType, system_name: obj?.system_name ?? null, label: obj?.label ?? null };
    setTable(next); setFields(null); setError(null);
    try {
      const r = await adminApi.fireberryObjectFields(objectType);
      setFields(r.fields);
      setMap(r.suggested);
    } catch { setError('לא ניתן לטעון את שדות הטבלה'); }
  }

  async function save(nextImport: FireberryImportSettings = imp) {
    if (!table) { toast('בחר טבלת לידים', 'danger'); return; }
    if (!map.phone) { toast('בחר את שדה הטלפון בטבלה', 'danger'); return; }
    if (nextImport.enabled && !nextImport.campaign_id) { toast('בחר קמפיין יעד לייבוא', 'danger'); return; }
    setSaving(true);
    try {
      const res = await adminApi.updateFireberry({
        lead_table: table, field_map: map,
        import: { enabled: nextImport.enabled, campaign_id: nextImport.campaign_id, interval_min: nextImport.interval_min, only_new: nextImport.only_new },
      });
      onSaved(res);
      if (res.import) setImp(res.import);
      toast('הטבלה, המיפוי והייבוא נשמרו', 'ok');
    } catch (e: unknown) {
      toast((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'שגיאה בשמירה', 'danger');
    } finally { setSaving(false); }
  }

  async function run(dryRun: boolean) {
    setRunning(dryRun ? 'preview' : 'run'); setResult(null);
    try {
      setResult(await adminApi.runFireberryImport({ dry_run: dryRun }));
    } catch (e: unknown) {
      setResult({ ok: false, message: (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'שגיאה בייבוא', fetched: 0, created: 0, updated: 0, skipped: 0 });
    } finally { setRunning(null); }
  }

  const fieldOptions = (allowEmpty: boolean) => [
    ...(allowEmpty ? [{ value: '', label: '— ללא —' }] : []),
    ...((fields ?? []).map((f) => ({ value: f.name, label: `${f.label} (${f.name})` }))),
  ];

  return (
    <div data-testid="fireberry-lead-table" style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-2)', padding: 12, marginTop: 8 }}>
      <div style={{ fontWeight: 600 }}>מאיזו טבלה לוקחים לידים</div>
      <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginTop: 2, marginBottom: 8 }}>
        הטבלה בפיירברי שבה יושבים הלידים (אנשי קשר, לקוחות או טבלה מותאמת אישית). היא משמשת גם ליצירת לקוחות מהאוטומציות וגם לייבוא לידים לקמפיין.
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'end', flexWrap: 'wrap' }}>
        <FormField label="טבלת הלידים בפיירברי">
          {objects ? (
            <FormSelect value={table?.object_type ?? ''} onChange={(e) => void chooseTable(Number(e.target.value))} aria-label="טבלת לידים">
              <option value="">בחר טבלה…</option>
              {objects.map((o) => <option key={o.object_type} value={o.object_type}>{o.label} ({o.system_name || o.object_type})</option>)}
            </FormSelect>
          ) : (
            <div style={{ fontSize: 'var(--t-sm)' }}>
              {table ? <>נבחר: <strong>{table.label ?? table.system_name ?? table.object_type}</strong> <code dir="ltr">#{table.object_type}</code></> : 'עדיין לא נבחרה טבלה'}
            </div>
          )}
        </FormField>
        <button type="button" className="btn" onClick={() => void loadObjects()} disabled={loadingObjects}>
          {loadingObjects ? 'טוען…' : objects ? 'רענן טבלאות' : 'טען טבלאות מפיירברי'}
        </button>
        {table && !fields && (
          <button type="button" className="btn ghost" onClick={() => void chooseTable(table.object_type)}>טען שדות למיפוי</button>
        )}
      </div>
      {error && <div style={{ color: 'var(--danger)', fontSize: 'var(--t-sm)', marginTop: 6 }}>{error}</div>}

      {table && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontWeight: 600, fontSize: 'var(--t-sm)' }}>מיפוי שדות {fields ? '' : '(טען שדות כדי לבחור מרשימה)'}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8, marginTop: 6 }}>
            {MAP_LABELS.map(({ key, label, hint }) => (
              <FormField key={key} label={label} hint={hint}>
                {fields ? (
                  <FormSelect value={map[key] ?? ''} onChange={(e) => setMap({ ...map, [key]: e.target.value || null })} aria-label={label}>
                    {fieldOptions(true).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </FormSelect>
                ) : (
                  <TextInput value={map[key] ?? ''} onChange={(e) => setMap({ ...map, [key]: e.target.value || null })} dir="ltr" placeholder="שם השדה בפיירברי" aria-label={label} />
                )}
              </FormField>
            ))}
          </div>

          {/* ── ייבוא לקמפיין ── */}
          <div style={{ marginTop: 12, padding: '10px 12px', background: 'var(--surface-2)', borderRadius: 'var(--r-2)', border: '1px solid var(--line)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600 }}>ייבוא לידים מהטבלה לקמפיין</div>
                <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginTop: 2 }}>
                  {imp.enabled ? `דלוק — כל ${imp.interval_min} דקות נמשכים לידים ${imp.only_new ? 'חדשים' : 'חדשים ומעודכנים'} לקמפיין` : 'כבוי — אפשר לייבא ידנית עם הכפתורים למטה'}
                  {imp.last_result ? ` · ריצה אחרונה: ${imp.last_result.message}` : ''}
                </div>
              </div>
              <Toggle checked={imp.enabled} onChange={(v) => setImp({ ...imp, enabled: v })} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 8, marginTop: 8 }}>
              <FormField label="קמפיין יעד">
                <FormSelect value={imp.campaign_id ?? ''} onChange={(e) => setImp({ ...imp, campaign_id: e.target.value ? Number(e.target.value) : null })} aria-label="קמפיין יעד">
                  <option value="">בחר קמפיין…</option>
                  {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </FormSelect>
              </FormField>
              <FormField label="תדירות (דקות)">
                <TextInput type="number" value={String(imp.interval_min)} onChange={(e) => setImp({ ...imp, interval_min: Math.max(1, Number(e.target.value) || 5) })} dir="ltr" aria-label="תדירות" />
              </FormField>
              <FormField label="מה מושכים">
                <FormSelect value={imp.only_new ? 'new' : 'all'} onChange={(e) => setImp({ ...imp, only_new: e.target.value === 'new' })} aria-label="מה מושכים">
                  <option value="new">רק רשומות חדשות (לפי תאריך יצירה)</option>
                  <option value="all">חדשות ומעודכנות (לפי תאריך עדכון)</option>
                </FormSelect>
              </FormField>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
              <button type="button" className="btn primary" onClick={() => void save()} disabled={saving}>{saving ? 'שומר…' : 'שמור טבלה ומיפוי'}</button>
              <button type="button" className="btn" onClick={() => void run(true)} disabled={running !== null}>{running === 'preview' ? 'בודק…' : 'תצוגה מקדימה'}</button>
              <button type="button" className="btn" onClick={() => void run(false)} disabled={running !== null}>{running === 'run' ? 'מייבא…' : 'ייבא עכשיו'}</button>
            </div>
            {result && (
              <div data-testid="fireberry-import-result" style={{ marginTop: 8, fontSize: 'var(--t-sm)', color: result.ok ? 'var(--text)' : 'var(--danger)' }}>
                <div>{result.message}</div>
                {result.preview && result.preview.length > 0 && (
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 6, fontSize: 'var(--t-xs)' }}>
                    <thead><tr>{['טלפון', 'שם', 'אימייל', 'מזהה בפיירברי'].map((h) => <th key={h} style={{ textAlign: 'start', padding: '4px 6px', color: 'var(--muted)' }}>{h}</th>)}</tr></thead>
                    <tbody>{result.preview.map((p, i) => (
                      <tr key={i} style={{ borderTop: '1px solid var(--line)' }}>
                        <td style={{ padding: '4px 6px' }} dir="ltr">{p.phone}</td><td style={{ padding: '4px 6px' }}>{p.name ?? '—'}</td>
                        <td style={{ padding: '4px 6px' }} dir="ltr">{p.email ?? '—'}</td><td style={{ padding: '4px 6px' }} dir="ltr">{p.external_id ?? '—'}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
